/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { getLinkedMapIds } from './selectors';
import { layerActions } from '../map/layer';
import type { CoreAppStore } from '../types';
import {
  getFeatureSyncLayersForPanel,
  upsertFeatureSyncLayer,
} from './syncGroups/featureSyncLayers';
import type { FeatureLayer } from '../map/layer/types';

export const useUpdateFeatureSyncLayers = (
  featureLayers: Record<string, FeatureLayer>,
  panelId?: string,
): void => {
  const linkedMapIds = useSelector(
    (state: CoreAppStore) => (panelId ? getLinkedMapIds(state, panelId) : []),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const store = useStore<CoreAppStore>();

  const stableLayersRef = useRef<Record<string, FeatureLayer>>(featureLayers);
  if (!isEqual(stableLayersRef.current, featureLayers)) {
    stableLayersRef.current = featureLayers;
  }
  const stableFeatureLayers = stableLayersRef.current;
  const managedLayerKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!panelId) {
      return;
    }

    const nextLayerKeys = new Set(Object.keys(stableFeatureLayers));
    const managedLayerKeys = new Set([
      ...managedLayerKeysRef.current,
      ...nextLayerKeys,
    ]);

    Object.entries(stableFeatureLayers).forEach(
      ([featureLayerKey, featureLayerData]) => {
        linkedMapIds.forEach((mapId) => {
          upsertFeatureSyncLayer(
            store.getState().layers,
            dispatch,
            panelId,
            featureLayerKey,
            mapId,
            featureLayerData,
          );
        });
      },
    );

    managedLayerKeysRef.current = managedLayerKeys;

    getFeatureSyncLayersForPanel(store.getState().layers, panelId).forEach(
      ({ layerId, mapId, featureLayerKey }) => {
        if (!managedLayerKeys.has(featureLayerKey)) {
          return;
        }

        if (
          !linkedMapIds.includes(mapId) ||
          !nextLayerKeys.has(featureLayerKey)
        ) {
          dispatch(layerActions.layerDelete({ layerId, mapId }));
        }
      },
    );
  }, [dispatch, linkedMapIds, panelId, stableFeatureLayers, store]);

  useEffect(
    () => (): void => {
      if (!panelId) {
        return;
      }

      getFeatureSyncLayersForPanel(store.getState().layers, panelId).forEach(
        ({ layerId, mapId, featureLayerKey }) => {
          if (!managedLayerKeysRef.current.has(featureLayerKey)) {
            return;
          }

          dispatch(layerActions.layerDelete({ layerId, mapId }));
        },
      );
      managedLayerKeysRef.current = new Set();
    },
    [dispatch, panelId, store],
  );
};
